<template>
  <div class="menu-container">
    <el-menu
      :default-active="activeMenuId"
      class="custom-menu"
      background-color="#fff"
      text-color="#303133"
      active-text-color="#e9ecfc"
      hover-background-color="#e9ecfc"
      :unique-opened="true"
    >
      <!-- 菜单内容保持不变 -->
      <template v-for="menu in menus" :key="menu.id.toString()">
        <!-- 有子菜单的情况 -->
        <el-sub-menu
          v-if="hasValidChildren(menu)"
          :index="menu.id.toString()"
          :disabled="menu.status !== '1'"
        >
          <template #title>
            <el-icon>
              <component :is="menu.icon" />
            </el-icon>
            <span>{{ menu.name }}</span>
          </template>

          <!-- 二级菜单 -->
          <template v-for="child in menu.children" :key="child.id.toString()">
            <!-- 二级有子菜单 -->
            <el-sub-menu
              v-if="hasValidChildren(child)"
              :index="child.id.toString()"
              :disabled="child.status !== '1'"
              class="second-level-submenu"
            >
              <template #title>
                <span>{{ child.name }}</span>
              </template>

              <!-- 三级菜单（只能是菜单项） -->
              <el-menu-item
                v-for="subChild in child.children"
                :key="subChild.id.toString()"
                :index="subChild.id.toString()"
                :disabled="subChild.status !== '1'"
                @click="navigate(subChild)"
              >
                <span>{{ subChild.name }}</span>
              </el-menu-item>
            </el-sub-menu>

            <!-- 二级没有子菜单 -->
            <el-menu-item
              v-else
              :index="child.id.toString()"
              :disabled="child.status !== '1'"
              @click="navigate(child)"
            >
              <span>{{ child.name }}</span>
            </el-menu-item>
          </template>
        </el-sub-menu>

        <!-- 一级没有子菜单 -->
        <el-menu-item
          v-else
          :index="menu.id.toString()"
          :disabled="menu.status !== '1'"
          @click="navigate(menu)"
        >
          <el-icon>
            <component :is="menu.icon" />
          </el-icon>
          <span>{{ menu.name }}</span>
        </el-menu-item>
      </template>
    </el-menu>
  </div>
</template>

<script setup>
// 脚本部分保持不变
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ElMessage } from 'element-plus'
import { useUserStore } from '@/store/modules/user'

const router = useRouter()
const route = useRoute()
const userStore = useUserStore()
const menus = ref([])

// 获取菜单数据
const fetchMenus = () => {
  if(userStore.user.systemid == 2) {
    menus.value = userStore.user.menus
  } else {
    menus.value = userStore.user.adminmenu
  }
}

// 过滤出有效的子菜单（状态为1且存在）
const hasValidChildren = (menu) => {
  return menu.children?.filter(child => child.status === '1').length > 0
}

// 计算属性：根据当前路由查找对应的菜单ID
const activeMenuId = computed(() => {
  const findMenuIdByPath = (items, path) => {
    for (const item of items) {
      if (item.path === path) {
        return item.id
      }
      if (item.children?.length) {
        const childId = findMenuIdByPath(item.children, path)
        if (childId) return childId
      }
    }
    return ''
  }

  return findMenuIdByPath(menus.value, route.path)
})

const navigate = (item) => {
  if (!item.status || item.status !== '1') {
    ElMessage.info('功能暂未开放')
    return
  }
  if (item.path) {
    router.push(item.path)
  }
}

onMounted(() => {
  fetchMenus()
})

// 监听用户ID变化
watch(
  () => userStore.user.systemid,
  (newVal, oldVal) => {
    if (newVal !== oldVal) {
      fetchMenus()
    }
  }
)
</script>

<style scoped>

:deep(.el-sub-menu__title:hover) {
  background-color: #e9ecfc !important;
}
/* 新增菜单容器样式 */
.menu-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* 基础样式调整 */
.custom-menu {
  border-right: none;
  flex: 1; /* 占满容器剩余空间 */
  max-height: 100%; /* 最大高度为容器高度 */
  overflow-y: auto; /* 启用垂直滚动 */
  overflow-x: hidden; /* 禁用水平滚动 */
  transition: all 0.3s ease;
}

/* 修复子菜单滚动问题 */
.custom-menu .el-menu {
  overflow: visible !important; /* 确保子菜单可以溢出父容器显示 */
}

/* 自定义滚动条样式优化 */
.custom-menu::-webkit-scrollbar {
  width: 5px;
}

.custom-menu::-webkit-scrollbar-track {
  background: transparent;
}

.custom-menu::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
}

.custom-menu::-webkit-scrollbar-thumb:hover {
  background-color: rgba(255, 255, 255, 0.4);
}

/* 其他样式保持不变 */
.custom-menu:not(.el-menu--collapse) {
  width: 220px;
}

.custom-menu > .el-sub-menu > .el-sub-menu__title,
.custom-menu > .el-menu-item {
  padding-left: 20px !important;
  font-weight: 500;
}

.custom-menu > .el-sub-menu > .el-sub-menu__title > .el-icon,
.custom-menu > .el-menu-item > .el-icon {
  margin-right: 12px;
  font-size: 18px;
}

.custom-menu .el-menu .el-sub-menu > .el-sub-menu__title,
.custom-menu .el-menu .el-menu-item {
  padding-left: 40px !important;
  background-color: #fff !important;
  font-weight: 400;
}

.custom-menu .el-menu .el-sub-menu > .el-sub-menu__title {
  padding-left: 36px !important;
}

.custom-menu .el-menu .el-menu .el-menu-item {
  padding-left: 60px !important;
}

.custom-menu .el-menu-item.is-active {
  background-color: #e9ecfc !important;
  color: #303133 !important;
}

.custom-menu .el-sub-menu.is-active > .el-sub-menu__title {
  color: #ffffff !important;
}

.custom-menu .el-sub-menu__title:hover,
.custom-menu .el-menu-item:hover {
  color: #303133 !important;
  background-color: #e9ecfc !important;
}



.custom-menu .el-menu-item.is-disabled,
.custom-menu .el-sub-menu.is-disabled .el-sub-menu__title {
  opacity: 0.5;
  cursor: not-allowed;
  color: #666 !important;
}

.custom-menu .el-menu {
  border-right: 1px solid #0a1d2f !important;
}

.custom-menu .el-menu .el-menu {
  border-right: 1px solid #0f263d !important;
}

.custom-menu > .el-sub-menu > .el-menu {
  background-color: #fff !important;
}

.custom-menu .el-menu .el-sub-menu > .el-menu {
  background-color: #fff !important;
}
</style>
